import type { UnityCall, UnityPayload } from './index';

export type BusinessCleanupKey =
  | 'powerArea'
  | 'energyOverview'
  | 'waterStation'
  | 'waterFlowmeter'
  | 'environmentMonitor'
  | 'lightGgd'
  | 'lightSun'
  | 'lightWorkshop';

const cleanupPayloadMap = new Map<BusinessCleanupKey, UnityPayload[]>();
let activeCleanupCallUnity: UnityCall | null = null;

const withTimeout = <T>(promise: Promise<T>, ms = 1500) =>
  Promise.race([
    promise,
    new Promise<T>((_, reject) => {
      window.setTimeout(() => reject(new Error(`cleanup timeout after ${ms}ms`)), ms);
    }),
  ]);

export const setUnityBusinessCleanupPayloads = (key: BusinessCleanupKey, payloads: UnityPayload[]) => {
  cleanupPayloadMap.set(key, payloads);
};

export const clearUnityBusinessCleanupPayloads = (key: BusinessCleanupKey) => {
  cleanupPayloadMap.delete(key);
};

export const setActiveUnityBusinessCleanupCaller = (callUnity: UnityCall | null) => {
  activeCleanupCallUnity = callUnity;
};

export const cleanupUnityBusinessPayloads = async (
  callUnity: UnityCall,
  keys: BusinessCleanupKey[],
  logPrefix: string
) => {
  const entries = keys
    .map(key => ({ key, payloads: cleanupPayloadMap.get(key) ?? [] }))
    .filter(entry => entry.payloads.length);

  if (!entries.length) return;

  console.info(`${logPrefix} cleanup previous unity business models`, {
    keys: entries.map(entry => entry.key),
    payloads: entries.flatMap(entry => entry.payloads),
  });

  const failedKeySet = new Set<BusinessCleanupKey>();

  await Promise.all(
    entries.flatMap(entry =>
      entry.payloads.map(payload =>
        withTimeout(callUnity(payload)).catch(error => {
          failedKeySet.add(entry.key);
          console.error(`${logPrefix} cleanup previous unity business model failed`, {
            key: entry.key,
            payload,
            error,
          });
        })
      )
    )
  );

  entries.forEach(entry => {
    if (!failedKeySet.has(entry.key)) {
      cleanupPayloadMap.delete(entry.key);
    }
  });
};

export const cleanupUnityBusinessPayloadsExcept = async (
  callUnity: UnityCall,
  keepKeys: BusinessCleanupKey[],
  logPrefix: string
) => {
  const keepKeySet = new Set(keepKeys);
  const keys = Array.from(cleanupPayloadMap.keys()).filter(key => !keepKeySet.has(key));
  await cleanupUnityBusinessPayloads(callUnity, keys, logPrefix);
};

export const cleanupAllUnityBusinessPayloads = async (logPrefix: string) => {
  if (!activeCleanupCallUnity) return;
  await cleanupUnityBusinessPayloads(activeCleanupCallUnity, Array.from(cleanupPayloadMap.keys()), logPrefix);
};
